'use client';

import { useEffect, useRef } from 'react';
import { useAnalytics } from './useAnalytics';
import { MTAG_EVENTS } from '../config/eventNames';
import { getImpressionById } from '../config/impressions';
import { getCurrentPageType } from '../config/pageConfig';
import { buildImpressionEvent } from '../utils/buildInteractionEvent';
import type { ImpressionEvent } from '@material-bank/analytics-events-ts/mtag';
export type ImpressionExtra = {
  sku?: string;
  productName?: string;
  brand?: string;
  cartId?: string;
  projectId?: string;
};
function buildImpressionPayload(analyticsId: string, extra: ImpressionExtra | undefined): ImpressionEvent | null {
  const config = getImpressionById(analyticsId);
  if (!config) {
    return null;
  }
  return buildImpressionEvent({
    impressionType: config.impression_type,
    // Resolved at fire time, as registered clicks do, so `page` carries one
    // vocabulary (the canonical page type) rather than repeating impression_type.
    page: getCurrentPageType() ?? '',
    legacyComponent: config.legacy_interaction_context,
    legacySection: config.legacy_interaction_feature
  }, {
    sku: extra?.sku,
    productName: extra?.productName,
    brand: extra?.brand,
    cartId: extra?.cartId,
    projectId: extra?.projectId
  });
}

// Fires a single `impression_event` when `isOpen` transitions to true (modals/sheets).
// Resets on close so reopening emits a fresh impression.
export function useModalImpression(isOpen: boolean, analyticsId: string, extra?: ImpressionExtra) {
  const {
    track,
    isReady
  } = useAnalytics();
  const trackedRef = useRef(false);
  const extraRef = useRef(extra);
  extraRef.current = extra;
  useEffect(() => {
    if (!isOpen) {
      trackedRef.current = false;
      return;
    }
    if (trackedRef.current || !isReady) {
      return;
    }
    trackedRef.current = true;
    const payload = buildImpressionPayload(analyticsId, extraRef.current);
    if (!payload) {
      return;
    }
    track(MTAG_EVENTS.IMPRESSION, payload);
  }, [isOpen, analyticsId, track, isReady]);
}